/**
 * The builder website's look: concrete-grey ground, charcoal ink, and the
 * builder's own colour (Brand Settings → theme colour) on buttons and
 * progress. Without one it wears site-board yellow. The black-and-yellow
 * hazard band stays whatever the colour: it is the site hoarding every
 * Kampala site puts up, and it says "construction" before a word is read.
 */

import type { CSSProperties } from "react";
import { formatIn } from "@/lib/config/currencies";
import type { WebForeign } from "@/lib/config/builderSite";

const HIVIZ = "#f2b300";
const INK = "#15191d";

function channel(hex: string, i: number): number {
  return parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
}

function luminance(hex: string): number {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(channel(hex, 0)) + 0.7152 * lin(channel(hex, 1)) + 0.0722 * lin(channel(hex, 2));
}

function darken(hex: string, keep: number): string {
  const c = [0, 1, 2].map((i) => Math.round(channel(hex, i) * keep).toString(16).padStart(2, "0"));
  return `#${c.join("")}`;
}

export function builderTheme(themeColor?: string): CSSProperties {
  const accent = /^#[0-9a-f]{6}$/i.test(String(themeColor ?? "")) ? String(themeColor) : HIVIZ;
  const lum = luminance(accent);
  return {
    "--b-accent": accent,
    // Text on the accent: whichever of white and charcoal reads better.
    "--b-on-accent": lum > 0.2 ? INK : "#ffffff",
    // The accent as text on white: a light colour is darkened until it reads.
    "--b-accent-ink": lum > 0.18 ? darken(accent, 0.5) : accent,
    "--b-ink": INK,
    "--b-ground": "#f3f4f2",
    "--b-panel": "#ffffff",
    "--b-line": "#d9ddd8",
    "--b-muted": "#57606a",
    "--b-hiviz": HIVIZ,
  } as CSSProperties;
}

const MONEY = new Intl.NumberFormat("en-UG", { maximumFractionDigits: 0 });

export const ugx = (n: number) => `UGX ${MONEY.format(Math.round(n))}`;

/** 280000000 → "UGX 280M", 1250000000 → "UGX 1.25B" */
export function ugxShort(n: number): string {
  const v = Math.round(n);
  if (v >= 1e9) return `UGX ${Number((v / 1e9).toFixed(2))}B`;
  if (v >= 1e6) return `UGX ${Number((v / 1e6).toFixed(1))}M`;
  return ugx(v);
}

/**
 * A price in the website's other currency (Nakasero Estates, Oct 2026):
 * "USD 495,000" as the builder set it, "≈ USD 75,676" when worked out at the
 * rate.
 */
export const foreignText = (f: WebForeign) => `${f.approx ? "≈ " : ""}${formatIn(f.amount, f.code)}`;

/** The short form a project board uses: "USD 1.2M", "≈ USD 75,676". */
export function foreignShort(f: WebForeign): string {
  const v = Math.round(f.amount);
  const n = v >= 1e9 ? `${f.code} ${Number((v / 1e9).toFixed(2))}B` : v >= 1e6 ? `${f.code} ${Number((v / 1e6).toFixed(1))}M` : formatIn(f.amount, f.code);
  return `${f.approx ? "≈ " : ""}${n}`;
}

export const floorLabel = (f: number | undefined) => (f === undefined ? "—" : f === 0 ? "Ground" : `Floor ${f}`);

/** Ask about a project or a unit: the enquiry form fills itself in. */
export const ENQUIRE_EVENT = "builder:enquire";

export function askAbout(project?: string, unit?: string): void {
  window.dispatchEvent(new CustomEvent(ENQUIRE_EVENT, { detail: { project, unit } }));
  // After the project window has closed and given the page its scroll back.
  window.setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
}
